import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getInvoiceLedger, getWarehouses, getProducts, apiCall, API_BASE_URL } from '../config/api';
import { saveBlob, toCsvCell } from '../utils/nativeFiles';
import useIsMobile from '../hooks/useIsMobile';

const STORAGE_KEY = 'invoiceDashboard_filters';

const InvoiceDashboard = () => {
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return saved.selectedWarehouses || [];
  });
  const [selectedSources, setSelectedSources] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return saved.selectedSources || [];
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [filters, setFilters] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return saved.filters || {
      date_from: '', date_to: '', order_id: '', invoice_number: '',
      sku_id: '', billing_name: '', gst_number: '', line_type: ''
    };
  });

  const sourceOptions = [
    { value: 'Online', label: 'Online' },
    { value: 'ZS B2B', label: 'ZS B2B' },
    { value: 'ZS B2C', label: 'ZS B2C' },
    { value: 'Channel', label: 'Channel' }
  ];

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    setIsSuperAdmin(userData.role === 'superAdmin');

    getWarehouses().then(res => {
      const list = res?.data || [];
      setWarehouses(list);
      if (list.length > 0) {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        const savedWh = saved.selectedWarehouses;
        const savedSrc = saved.selectedSources || [];
        const savedFilters = saved.filters;

        // Use saved filters if available, otherwise default to current month
        if (savedWh && savedWh.length && savedFilters && (savedFilters.date_from || savedFilters.order_id || savedFilters.invoice_number)) {
          setSelectedWarehouses(savedWh);
          setSelectedSources(savedSrc);
          setFilters(savedFilters);
          loadData(savedFilters, savedWh.map(w => w.value), savedSrc.map(s => s.value));
        } else {
          const firstId = String(list[0].id);
          const whSelection = [{ value: firstId, label: list[0].name }];
          setSelectedWarehouses(whSelection);
          const now = new Date();
          const dateFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
          const dateTo = now.toISOString().split('T')[0];
          const initFilters = { ...filters, date_from: dateFrom, date_to: dateTo };
          setFilters(initFilters);
          loadData(initFilters, [firstId], []);
        }
      }
    }).catch(() => toast.error('Failed to load warehouses'));

    getProducts().then(res => {
      if (res.success && res.data) setCatalogProducts(res.data || []);
    }).catch(() => {});
  }, []);

  const loadData = async (f = filters, whIds = selectedWarehouses.map(w => w.value), sources = selectedSources.map(s => s.value)) => {
    if (!whIds.length) { toast.error('Please select at least one warehouse'); return; }
    setLoading(true);
    try {
      const params = { ...f, warehouse_id: whIds.join(',') };
      if (sources.length) params.source = sources.join(',');
      const res = await getInvoiceLedger(params);
      setRows(res?.data || []);
    } catch (err) { toast.error('Failed to load invoice ledger'); }
    finally { setLoading(false); }
  };

  const handleFilterChange = (e) => setFilters(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleSearch = () => {
    // Persist filters to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, selectedWarehouses, selectedSources }));
    loadData(filters, selectedWarehouses.map(w => w.value), selectedSources.map(s => s.value));
  };
  const handleClear = () => {
    const cleared = { date_from: '', date_to: '', order_id: '', invoice_number: '', sku_id: '', billing_name: '', gst_number: '', line_type: '' };
    setFilters(cleared);
    setSelectedSources([]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ filters: cleared, selectedWarehouses, selectedSources: [] }));
    loadData(cleared, selectedWarehouses.map(w => w.value), []);
  };

  // Edit modal logic
  const openEditModal = (invoiceNumber) => {
    const items = rows.filter(r => r.invoice_number === invoiceNumber);
    setEditingInvoice(invoiceNumber);
    setEditItems(items.map(item => ({ ...item, _action: 'existing' })));
    setShowEditModal(true);
  };

  const handleEditItemChange = (index, field, value) => {
    setEditItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleAddEditItem = () => {
    const baseItem = editItems[0] || {};
    setEditItems(prev => [...prev, {
      _action: 'new', invoice_number: editingInvoice, invoice_date: baseItem.invoice_date || '',
      order_id: baseItem.order_id || '', warehouse_id: baseItem.warehouse_id || '',
      billing_name: baseItem.billing_name || '', gst_number: baseItem.gst_number || '',
      line_type: 'SKU', sku_id: '', sku_name: '', price: 0, qty: 1,
      invoice_value: 0, gst_amount: 0, total: 0, is_cancellation: false, month: baseItem.month || ''
    }]);
  };

  const handleRemoveEditItem = (index) => {
    setEditItems(prev => prev.map((item, i) => i === index ? { ...item, _action: 'delete' } : item));
  };

  const handleSaveEdit = async () => {
    try {
      const items = editItems
        .filter(item => item._action !== 'delete')
        .map(({ _action, id, order, ...item }) => item);

      await apiCall(`${API_BASE_URL}/gstzen-invoices/ledger/overwrite`, {
        method: 'POST',
        body: JSON.stringify({ invoiceNumber: editingInvoice, items })
      });

      toast.success('Invoice data updated');
      setShowEditModal(false);
      handleSearch();
    } catch (err) { toast.error(err.message || 'Failed to save'); }
  };

  const handleDeleteInvoice = async (invoiceNumber) => {
    if (!window.confirm(`Delete ALL ledger entries for invoice ${invoiceNumber}? This cannot be undone.`)) return;
    try {
      await apiCall(`${API_BASE_URL}/gstzen-invoices/ledger/overwrite`, {
        method: 'POST',
        body: JSON.stringify({ invoiceNumber, items: [] })
      });
      toast.success('Invoice entries deleted');
      handleSearch();
    } catch (err) { toast.error(err.message || 'Failed to delete'); }
  };

  const handleDownload = () => {
    if (!rows.length) { toast.error('No data to download'); return; }
    const headers = ['Date', 'Order ID', 'Invoice No.', 'SKU ID', 'SKU Name', 'Billing Name', 'GST Number', 'Price', 'Qty', 'Invoice Value', 'GST', 'Total', 'Type', 'Cancellation'];
    const csvRows = rows.map(r => [r.invoice_date, r.order_id, r.invoice_number, r.sku_id, r.sku_name, r.billing_name, r.gst_number, r.price, r.qty, r.invoice_value, r.gst_amount, r.total, r.line_type, r.is_cancellation ? 'Yes' : 'No'].map(toCsvCell).join(','));
    const csv = [headers.map(toCsvCell).join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveBlob(blob, `invoice_ledger_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    invoice_value: acc.invoice_value + (parseFloat(r.invoice_value) || 0),
    gst_amount: acc.gst_amount + (parseFloat(r.gst_amount) || 0),
    total: acc.total + (parseFloat(r.total) || 0)
  }), { invoice_value: 0, gst_amount: 0, total: 0 }), [rows]);

  const fmt = (n) => n === null || n === undefined ? '—' : parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const lineTypeBadge = (type, isCancellation) => {
    const colors = { SKU: '#0d6efd', Transportation: '#6f42c1', Handling: '#fd7e14', Discount: '#dc3545', Platform: '#20c997' };
    return <span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 7px', borderRadius: '4px', backgroundColor: colors[type] || '#6c757d', color: '#fff', opacity: isCancellation ? 0.6 : 1 }}>{isCancellation ? `↩ ${type}` : type}</span>;
  };

  const columns = [
    { name: 'Date', selector: r => r.invoice_date, sortable: true, width: '100px', cell: r => <span style={{ fontSize: '12px' }}>{r.invoice_date || '—'}</span> },
    { name: 'Order ID', selector: r => r.order_id, sortable: true, width: '130px', cell: r => <a href={`/orders/${r.order_id}`} target={isMobile ? undefined : '_blank'} rel="noreferrer" style={{ fontSize: '12px', color: '#0d6efd', fontWeight: '600' }}>{r.order_id}</a> },
    { name: 'Hub', selector: r => r.warehouse_id, sortable: true, width: '100px', cell: r => { const wh = warehouses.find(w => w.id == r.warehouse_id); return <span style={{ fontSize: '11px' }}>{wh?.name || r.warehouse_id || '—'}</span>; } },
    { name: 'Source', selector: r => r.order?.source, sortable: true, width: '90px', cell: r => <span style={{ fontSize: '11px' }}>{r.order?.source || '—'}</span> },
    { name: 'Invoice No.', selector: r => r.invoice_number, sortable: true, width: '140px', cell: r => <span style={{ fontSize: '12px', fontWeight: '600' }}>{r.invoice_number}</span> },
    { name: 'Type', selector: r => r.line_type, sortable: true, width: '120px', cell: r => lineTypeBadge(r.line_type, r.is_cancellation) },
    { name: 'SKU ID', selector: r => r.sku_id, sortable: true, width: '160px', cell: r => <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>{r.sku_id || '—'}</span> },
    { name: 'SKU Name', selector: r => r.sku_name, sortable: true, width: '200px', cell: r => <span style={{ fontSize: '11px' }}>{r.sku_name || '—'}</span> },
    { name: 'Billing Name', selector: r => r.billing_name, sortable: true, width: '160px', cell: r => <span style={{ fontSize: '11px' }}>{r.billing_name || '—'}</span> },
    { name: 'GST No.', selector: r => r.gst_number, sortable: true, width: '150px', cell: r => <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>{r.gst_number || '—'}</span> },
    { name: 'Price', selector: r => r.price, sortable: true, width: '100px', right: true, cell: r => <span style={{ fontSize: '12px' }}>₹{fmt(r.price)}</span> },
    { name: 'Qty', selector: r => r.qty, sortable: true, width: '70px', right: true, cell: r => <span style={{ fontSize: '12px' }}>{r.qty}</span> },
    { name: 'Inv. Value', selector: r => r.invoice_value, sortable: true, width: '110px', right: true, cell: r => <span style={{ fontSize: '12px' }}>₹{fmt(r.invoice_value)}</span> },
    { name: 'GST', selector: r => r.gst_amount, sortable: true, width: '100px', right: true, cell: r => <span style={{ fontSize: '12px' }}>₹{fmt(r.gst_amount)}</span> },
    { name: 'Total', selector: r => r.total, sortable: true, width: '110px', right: true, cell: r => <span style={{ fontSize: '12px', fontWeight: '700', color: parseFloat(r.total) < 0 ? '#dc3545' : '#198754' }}>₹{fmt(r.total)}</span> },
    ...(isSuperAdmin ? [{
      name: 'Actions', width: '120px', cell: r => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={() => openEditModal(r.invoice_number)} style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 6px', cursor: 'pointer', border: '1px solid #0d6efd', borderRadius: '3px', background: 'transparent', color: '#0d6efd' }}>Edit</button>
          <button onClick={() => handleDeleteInvoice(r.invoice_number)} style={{ fontSize: isMobile ? '13px' : '10px', padding: isMobile ? '8px 12px' : '2px 6px', cursor: 'pointer', border: '1px solid #dc3545', borderRadius: '3px', background: 'transparent', color: '#dc3545' }}>Delete</button>
        </div>
      )
    }] : [])
  ];

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Invoice Dashboard</h2>
          {isSuperAdmin && <button onClick={handleDownload} className="btn btn-primary" style={{ fontSize: '12px' }}>⬇ Download CSV</button>}
        </div>

        {/* Hub multi-select */}
        <div style={{ marginBottom: '12px', display: 'flex', gap: '16px', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px' }}>Hubs</label>
            <Select
              isMulti
              value={selectedWarehouses}
              onChange={(val) => setSelectedWarehouses(val || [])}
              options={warehouses.map(w => ({ value: String(w.id), label: w.name }))}
              placeholder="Select hubs..."
              className="react-select-container"
              menuPortalTarget={document.body}
              styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px' }}>Source</label>
            <Select
              isMulti
              value={selectedSources}
              onChange={(val) => setSelectedSources(val || [])}
              options={sourceOptions}
              placeholder="All sources..."
              className="react-select-container"
              isSearchable={!isMobile || sourceOptions.length > 8}
              menuPortalTarget={document.body}
              styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
            />
          </div>
        </div>

        {/* Filters */}
        <div className={isMobile ? `filters-card${!filtersOpen ? ' mobile-collapsed' : ''}` : undefined} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          {isMobile && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Filters{Object.values(filters).filter(Boolean).length ? ` (${Object.values(filters).filter(Boolean).length})` : ''}
              </span>
              <button type="button" className="btn btn-secondary mobile-filters-toggle" onClick={() => setFiltersOpen((open) => !open)}>
                {filtersOpen ? 'Hide filters' : 'Filters'}
              </button>
            </div>
          )}
          {[
            { label: 'Date From', name: 'date_from', type: 'date' },
            { label: 'Date To', name: 'date_to', type: 'date' },
            { label: 'Order ID', name: 'order_id', type: 'text', placeholder: 'ZNX-...' },
            { label: 'Invoice No.', name: 'invoice_number', type: 'text', placeholder: 'UP/25-26/...' },
            { label: 'SKU ID', name: 'sku_id', type: 'text', placeholder: 'DSP-ADA-...' },
            { label: 'Billing Name', name: 'billing_name', type: 'text', placeholder: 'Company name' },
            { label: 'GST Number', name: 'gst_number', type: 'text', placeholder: '09XXXXX...' },
          ].map(f => (
            <div key={f.name}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '3px' }}>{f.label}</label>
              <input type={f.type} name={f.name} value={filters[f.name]} onChange={handleFilterChange} placeholder={f.placeholder || ''} className="form-control" style={{ fontSize: '12px', padding: '5px 8px' }} />
            </div>
          ))}
          <div>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '3px' }}>Line Type</label>
            <select name="line_type" value={filters.line_type} onChange={handleFilterChange} className="form-control" style={{ fontSize: '12px', padding: '5px 8px' }}>
              <option value="">All</option>
              <option value="SKU">SKU</option>
              <option value="Transportation">Transportation</option>
              <option value="Handling">Handling</option>
              <option value="Discount">Discount</option>
              <option value="Platform">Platform Fees</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <button onClick={handleSearch} className="btn btn-primary" style={{ fontSize: '12px', padding: '5px 14px' }}>Search</button>
            <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: '12px', padding: '5px 14px' }}>Clear</button>
          </div>
        </div>

        {/* Summary */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          {[
            { label: 'Rows', value: rows.length, color: '#0d6efd' },
            { label: 'Invoice Value', value: `₹${fmt(totals.invoice_value)}`, color: '#198754' },
            { label: 'GST', value: `₹${fmt(totals.gst_amount)}`, color: '#fd7e14' },
            { label: 'Total', value: `₹${fmt(totals.total)}`, color: '#6f42c1' }
          ].map(c => (
            <div key={c.label} style={{ flex: 1, minWidth: '140px', padding: '10px 14px', background: '#f8f9fa', borderRadius: '6px', borderLeft: `3px solid ${c.color}` }}>
              <div style={{ fontSize: '11px', color: '#6c757d' }}>{c.label}</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>

        <DataTable columns={columns} data={rows} progressPending={loading} pagination paginationPerPage={50} paginationRowsPerPageOptions={[25, 50, 100, 200]} noDataComponent={<div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>No invoice ledger entries found</div>} dense customStyles={{ headRow: { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' } }, headCells: { style: { fontWeight: '600', color: '#495057', fontSize: '11px', textTransform: 'uppercase' } } }} />
      </div>

      {/* Edit Invoice Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>Edit Invoice: {editingInvoice}</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12px', fontWeight: '600' }}>Invoice Date:</label>
                <input
                  type="date"
                  value={editItems[0]?.invoice_date || ''}
                  onChange={e => {
                    const newDate = e.target.value;
                    setEditItems(prev => prev.map(item => ({ ...item, invoice_date: newDate })));
                  }}
                  className="form-control"
                  style={{ width: '180px', fontSize: '12px' }}
                />
                <label style={{ fontSize: '12px', fontWeight: '600', marginLeft: '12px' }}>GST Number:</label>
                <input
                  type="text"
                  value={editItems[0]?.gst_number || ''}
                  onChange={e => {
                    const newGst = e.target.value.toUpperCase();
                    setEditItems(prev => prev.map(item => ({ ...item, gst_number: newGst })));
                  }}
                  className="form-control"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{ width: '200px', fontSize: '12px', fontFamily: 'monospace' }}
                  placeholder="09XXXXX0000X1Z5"
                  maxLength="15"
                />
              </div>
              <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '6px' }}>Type</th>
                    <th style={{ padding: '6px' }} colSpan="2">Product</th>
                    <th style={{ padding: '6px' }}>Price</th>
                    <th style={{ padding: '6px' }}>Qty</th>
                    <th style={{ padding: '6px' }}>Inv. Value</th>
                    <th style={{ padding: '6px' }}>GST</th>
                    <th style={{ padding: '6px' }}>Total</th>
                    <th style={{ padding: '6px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {editItems.filter(item => item._action !== 'delete').map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '4px' }}>
                        <select value={item.line_type} onChange={e => {
                          const newType = e.target.value;
                          handleEditItemChange(idx, 'line_type', newType);
                          if (newType !== 'SKU') {
                            handleEditItemChange(idx, 'sku_id', newType);
                            handleEditItemChange(idx, 'sku_name', newType === 'Transportation' ? 'Transportation' : newType === 'Handling' ? 'Handling Charges' : newType === 'Discount' ? 'Discount' : 'Platform Charges');
                          }
                        }} style={{ fontSize: '11px', width: '110px' }}>
                          <option value="SKU">SKU</option>
                          <option value="Transportation">Transport</option>
                          <option value="Handling">Handling</option>
                          <option value="Discount">Discount</option>
                          <option value="Platform">Platform Fees</option>
                        </select>
                      </td>
                      <td style={{ padding: '4px' }} colSpan="2">
                        {item.line_type === 'SKU' ? (
                          <select value={item.sku_id || ''} onChange={e => {
                            const selectedProduct = catalogProducts.find(p => p.skuCode === e.target.value);
                            handleEditItemChange(idx, 'sku_id', e.target.value);
                            if (selectedProduct) handleEditItemChange(idx, 'sku_name', selectedProduct.name);
                          }} style={{ fontSize: '11px', width: '240px' }}>
                            <option value="">Select Product</option>
                            {item.sku_id && !catalogProducts.find(p => p.skuCode === item.sku_id) && (
                              <option value={item.sku_id}>{item.sku_id} - {item.sku_name}</option>
                            )}
                            {catalogProducts.map(p => (
                              <option key={p.skuCode} value={p.skuCode}>{p.skuCode} - {p.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic', padding: '4px' }}>
                            {item.line_type === 'Transportation' ? 'Transportation' : item.line_type === 'Handling' ? 'Handling Charges' : item.line_type === 'Discount' ? 'Discount' : 'Platform Charges'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '4px' }}><input type="number" step="any" value={item.price ?? ''} onChange={e => handleEditItemChange(idx, 'price', e.target.value)} style={{ fontSize: '11px', width: '70px' }} /></td>
                      <td style={{ padding: '4px' }}><input type="number" step="any" value={item.qty ?? ''} onChange={e => handleEditItemChange(idx, 'qty', e.target.value)} style={{ fontSize: '11px', width: '50px' }} /></td>
                      <td style={{ padding: '4px' }}><input type="number" step="any" value={item.invoice_value ?? ''} onChange={e => handleEditItemChange(idx, 'invoice_value', e.target.value)} style={{ fontSize: '11px', width: '80px' }} /></td>
                      <td style={{ padding: '4px' }}><input type="number" step="any" value={item.gst_amount ?? ''} onChange={e => handleEditItemChange(idx, 'gst_amount', e.target.value)} style={{ fontSize: '11px', width: '70px' }} /></td>
                      <td style={{ padding: '4px' }}><input type="number" step="any" value={item.total ?? ''} onChange={e => handleEditItemChange(idx, 'total', e.target.value)} style={{ fontSize: '11px', width: '80px' }} /></td>
                      <td style={{ padding: '4px' }}><button onClick={() => handleRemoveEditItem(idx)} style={{ fontSize: isMobile ? '13px' : '10px', color: '#dc3545', cursor: 'pointer', border: 'none', background: 'none', ...(isMobile ? { padding: '8px 10px' } : {}) }}>✕ Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <button onClick={handleAddEditItem} style={{ marginTop: '10px', fontSize: isMobile ? '13px' : '12px', padding: isMobile ? '8px 12px' : '4px 10px', cursor: 'pointer' }} className="btn btn-info">+ Add Item</button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn btn-success" onClick={handleSaveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDashboard;
